/**
 * « Outils de la Boîte à outils » d'une SKILLCARD — fiche auteur (table
 * `card_tools`, migration 0551, lien N-N : une carte a plusieurs outils, un
 * outil sert plusieurs cartes).
 *
 * L'auteur relie, ordonne et retire des outils ; « Suggérés » propose ceux que
 * le titre, le sujet ou le domaine de la carte désignent (mêmes règles que le
 * pré-remplissage). Les élèves les ouvrent depuis la page de la carte et la
 * fenêtre Cours du player ; dans la boîte d'une question, ils passent en tête.
 * Sans outil relié, la boîte choisit seule. Lien vivant : rien à republier.
 */

import { useMemo, useState } from 'react';
import { ArrowUp, Plus, Shapes, X } from 'lucide-react';
import {
  TOOLS,
  TOOL_ICONS,
  ToolboxSheet,
  contextForCards,
  linkedTools,
  suggestedToolsForCard,
  toolFitsLevel,
  toolLevelForCard,
  toolSuitsCard,
  type ToolDef,
  type ToolId,
} from '@/features/toolbox';
import { useCardTools, useSetCardTools } from '@/lib/cards/card-tools';

export interface CardToolsEditorCard {
  id: string;
  title: string;
  topic: string | null;
  domain_label: string | null;
  levels: string[];
}

const selectClass =
  'border-ink-200 text-ink-900 focus:border-primary-400 min-h-11 w-full rounded-lg border bg-white px-3 text-sm outline-none';

export function CardToolsEditor({
  card,
  readOnly = false,
}: {
  card: CardToolsEditorCard;
  readOnly?: boolean;
}) {
  const query = useCardTools(card.id);
  const save = useSetCardTools(card.id);
  const [error, setError] = useState<string | null>(null);
  const [trying, setTrying] = useState<ToolId | null>(null);

  const linked = useMemo(() => linkedTools(query.data), [query.data]);
  const suggested = useMemo(
    () =>
      suggestedToolsForCard(card)
        .map((id) => TOOLS.find((t) => t.id === id)!)
        .filter((t) => !linked.includes(t)),
    [card, linked],
  );
  const rest = TOOLS.filter((t) => !linked.includes(t));
  // un outil d'une autre matière (chimie pour une carte de maths…) va à la fin
  const suits = rest.filter((t) => toolSuitsCard(t, card));
  const ofLevels = suits.filter((t) => card.levels.some((l) => toolFitsLevel(t, l)));
  const otherLevels = suits.filter((t) => !ofLevels.includes(t));
  const otherSubject = rest.filter((t) => !suits.includes(t));

  const write = async (next: readonly ToolDef[]) => {
    setError(null);
    try {
      await save.mutateAsync(next.map((t) => t.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const add = (t: ToolDef) => void write([...linked, t]);
  const remove = (t: ToolDef) => void write(linked.filter((x) => x !== t));
  const moveUp = (i: number) => {
    const next = [...linked];
    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
    void write(next);
  };

  const busy = save.isPending || readOnly;

  return (
    <div
      className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4"
      data-testid="card-tools-editor"
    >
      <div className="space-y-1">
        <h2 className="text-ink-900 font-display flex items-center gap-2 text-base font-bold">
          <Shapes className="text-primary-500 h-5 w-5" aria-hidden />
          Outils de la Boîte à outils
        </h2>
        <p className="text-ink-500 text-sm">
          Les élèves les ouvrent depuis la page de la carte et la fenêtre Cours ; dans un exercice,
          ils passent en tête de la boîte. Sans outil relié, la boîte choisit seule.
        </p>
      </div>

      {query.isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : linked.length === 0 ? (
        <p className="text-ink-500 text-sm">Aucun outil relié.</p>
      ) : (
        <ul className="space-y-1" aria-label="Outils reliés">
          {linked.map((t, i) => {
            const Icon = TOOL_ICONS[t.id];
            return (
              <li key={t.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setTrying(t.id)}
                  title="Essayer l’outil"
                  className="hover:bg-ink-50 flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-lg px-2 text-left text-sm font-semibold"
                >
                  <Icon className="text-primary-600 h-4 w-4 shrink-0" aria-hidden />
                  <span className="text-ink-900 truncate">{t.title}</span>
                </button>
                {i > 0 && (
                  <button
                    type="button"
                    aria-label={`Monter ${t.title}`}
                    disabled={busy}
                    onClick={() => moveUp(i)}
                    className="text-ink-400 hover:text-ink-700 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg disabled:opacity-40"
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden />
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`Retirer ${t.title}`}
                  disabled={busy}
                  onClick={() => remove(t)}
                  className="text-ink-400 hover:text-accent-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg disabled:opacity-40"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && suggested.length > 0 && (
        <div className="space-y-1">
          <p className="text-ink-500 text-xs font-semibold">Suggérés pour cette carte</p>
          <ul className="flex flex-wrap gap-2">
            {suggested.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => add(t)}
                  aria-label={`Relier ${t.title}`}
                  className="border-primary-300 text-primary-700 hover:bg-primary-50 inline-flex min-h-11 items-center gap-1.5 rounded-full border border-dashed px-3 text-sm font-semibold disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  {t.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!readOnly && rest.length > 0 && (
        <label className="block space-y-1">
          <span className="text-ink-500 text-xs font-semibold">Relier un autre outil</span>
          <select
            className={selectClass}
            value=""
            disabled={busy}
            onChange={(e) => {
              const t = TOOLS.find((x) => x.id === e.target.value);
              if (t) add(t);
            }}
          >
            <option value="">Choisir un outil…</option>
            {ofLevels.length > 0 && (
              <optgroup label="Aux niveaux de la carte">
                {ofLevels.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </optgroup>
            )}
            {otherLevels.length > 0 && (
              <optgroup label="Autres niveaux">
                {otherLevels.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </optgroup>
            )}
            {otherSubject.length > 0 && (
              <optgroup label="Autre matière">
                {otherSubject.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
      )}

      {error && (
        <p role="alert" className="text-accent-600 text-sm">
          {error}
        </p>
      )}

      {trying && (
        <ToolboxSheet
          context={contextForCards(
            [{ ...card, tools: linked.map((t) => t.id) }],
            toolLevelForCard(card.levels, null),
          )}
          initialTool={trying}
          featuredTitle="Pour cette SKILLCARD"
          onClose={() => setTrying(null)}
        />
      )}
    </div>
  );
}
